/**
 * EfolderSidebar - Collapsible sidebar with search, filters, sort, and document tree
 *
 * Design: All controls are in the sidebar so the PDF preview can take full height
 */

import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
  Calendar,
  HardDrive,
  CheckCircle,
  Search,
  Filter,
  X,
  Archive,
  Copy,
  CheckSquare,
  Download,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { EfolderTreeView } from './EfolderTreeView';
import type {
  EncompassDocumentWithAttachments,
  EncompassAttachment,
  EncompassDocumentGroup,
  EfolderGroupingMode
} from '@/types/encompass-documents';
import type {
  EfolderSort,
  EfolderSortField,
  EfolderFilters,
  EfolderStatusFilter,
  EfolderFileTypeFilter,
  EfolderDateRangeFilter
} from '@/hooks/useEfolderFilters';

interface EfolderSidebarProps {
  documents: EncompassDocumentWithAttachments[];
  groupedDocuments: EncompassDocumentGroup[];
  selectedAttachmentId: string | null;
  onSelectAttachment: (attachment: EncompassAttachment) => void;
  onViewAttachment: (attachment: EncompassAttachment) => void;
  onDownloadAttachment: (attachment: EncompassAttachment) => void;
  sort: EfolderSort;
  onSortFieldChange: (field: EfolderSortField) => void;
  onSortDirectionToggle: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  filteredCount: number;
  totalCount: number;
  // Filter props
  filters: EfolderFilters;
  onFilterChange: <K extends keyof EfolderFilters>(key: K, value: EfolderFilters[K]) => void;
  onResetFilters: () => void;
  inactiveCount: number;
  // Copy mode props
  isCopyMode: boolean;
  checkedAttachments: Set<string>;
  onEnterCopyMode: () => void;
  onExitCopyMode: () => void;
  onToggleCheck: (attachmentId: string) => void;
  onSelectAllDownloaded: () => void;
  onOpenCopyModal: () => void;
  /** Number of downloaded (copyable) attachments */
  downloadedCount: number;
  /** Number of selected items hidden by current filters (Phase 3) */
  hiddenSelectionsCount?: number;
  /** Clear hidden selections callback (Phase 3) */
  onClearHiddenSelections?: () => void;
  /** Phase 6: Toggle all checkboxes in a group */
  onToggleGroupCheck?: (groupName: string) => void;
  /** Phase 6: Toggle all checkboxes in a folder */
  onToggleFolderCheck?: (folderId: string) => void;
  /** Phase 6: Get selection state for a group (none/partial/all) */
  getGroupSelectionState?: (groupName: string) => 'none' | 'partial' | 'all';
  /** Phase 6: Get selection state for a folder (none/partial/all) */
  getFolderSelectionState?: (folderId: string) => 'none' | 'partial' | 'all';
  /** Get count of copyable (downloaded + active) attachments for a group */
  getCopyableCountForGroup?: (groupName: string) => number;
  /** Bulk download callback */
  onBulkDownload?: () => void;
  /** Whether bulk download is in progress */
  downloading?: boolean;
  /** True while the debounced content-search RPC is in flight (shown as a small spinner). */
  contentSearchLoading?: boolean;
  /** Set when the content-search RPC failed (shown as a small "Content search unavailable" note). */
  contentSearchError?: string | null;
}

// Sort field options with icons
const SORT_OPTIONS: { value: EfolderSortField; label: string; icon: React.ReactNode }[] = [
  { value: 'name', label: 'Name', icon: <FileText className="h-3 w-3" /> },
  { value: 'date', label: 'Date', icon: <Calendar className="h-3 w-3" /> },
  { value: 'size', label: 'Size', icon: <HardDrive className="h-3 w-3" /> },
  { value: 'status', label: 'Status', icon: <CheckCircle className="h-3 w-3" /> }
];

// Filter option configs
const STATUS_OPTIONS: { value: EfolderStatusFilter; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'downloaded', label: 'Ready' },
  { value: 'pending', label: 'Pending' },
  { value: 'pending_worker', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'unavailable', label: 'Unavailable' }
];

const FILE_TYPE_OPTIONS: { value: EfolderFileTypeFilter; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'pdf', label: 'PDF' },
  { value: 'image', label: 'Images' },
  { value: 'other', label: 'Other' }
];

const DATE_RANGE_OPTIONS: { value: EfolderDateRangeFilter; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: '7days', label: 'Last 7 days' },
  { value: '30days', label: 'Last 30 days' },
  { value: '90days', label: 'Last 90 days' }
];

const VIEW_MODE_OPTIONS: { value: EfolderGroupingMode; label: string; description: string }[] = [
  { value: 'groups', label: 'Grouped', description: 'Category > Folder > Documents' },
  { value: 'groups-flat', label: 'Grouped (Flat)', description: 'Category > Documents' },
  { value: 'flat', label: 'Flat', description: 'Folder > Documents' }
];

export function EfolderSidebar({
  documents,
  groupedDocuments,
  selectedAttachmentId,
  onSelectAttachment,
  onViewAttachment,
  onDownloadAttachment,
  sort,
  onSortFieldChange,
  onSortDirectionToggle,
  isCollapsed,
  onToggleCollapse,
  filteredCount,
  totalCount,
  filters,
  onFilterChange,
  onResetFilters,
  inactiveCount,
  isCopyMode,
  checkedAttachments,
  onEnterCopyMode,
  onExitCopyMode,
  onToggleCheck,
  onSelectAllDownloaded,
  onOpenCopyModal,
  downloadedCount,
  hiddenSelectionsCount = 0,
  onClearHiddenSelections,
  onToggleGroupCheck,
  onToggleFolderCheck,
  getGroupSelectionState,
  getFolderSelectionState,
  getCopyableCountForGroup,
  onBulkDownload,
  downloading = false,
  contentSearchLoading = false,
  contentSearchError = null
}: EfolderSidebarProps) {
  // Count active filters (excluding search)
  const activeFilterCount = [
    filters.status !== 'all',
    filters.fileType !== 'all',
    filters.dateRange !== 'all',
    filters.showInactive,
    filters.groupingMode !== 'groups' // Count if not using default grouped view
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0 || filters.searchQuery.trim() !== '';

  // Collapsed state - show only toggle button
  if (isCollapsed) {
    return (
      <div className="flex flex-col items-center py-4 h-full bg-muted/30">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleCollapse}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Expand sidebar</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Vertical document count indicator */}
        <div className="mt-4 flex flex-col items-center gap-1">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground writing-mode-vertical">
            {filteredCount}
          </span>
        </div>
      </div>
    );
  }

  // Expanded state
  return (
    <div className="flex flex-col h-full bg-muted/20">
      {/* Sidebar header with search, filters, sort */}
      <div className="flex flex-col gap-2 p-2 border-b bg-background/50">
        {/* Search row. Searches file name, folder, and (debounced) the
            extracted text inside downloaded documents. */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search file name, folder, or document text..."
            value={filters.searchQuery}
            onChange={(e) => onFilterChange('searchQuery', e.target.value)}
            className="pl-7 pr-12 h-7 text-xs"
            aria-busy={contentSearchLoading || undefined}
          />
          {contentSearchLoading && (
            <span
              className="absolute right-6 top-1/2 -translate-y-1/2"
              aria-label="Searching document text"
            >
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            </span>
          )}
          {filters.searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-0.5 top-1/2 -translate-y-1/2 h-5 w-5 p-0"
              onClick={() => onFilterChange('searchQuery', '')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        {contentSearchError && (
          <div className="text-[10px] text-amber-700" role="status">
            Content search unavailable — file name + folder match still works.
          </div>
        )}

        {/* Controls row: Filters + Sort + Collapse */}
        <div className="flex items-center gap-1">
          {/* Filters popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  'h-7 gap-1 text-xs px-2',
                  activeFilterCount > 0 && 'border-primary'
                )}
              >
                <Filter className="h-3 w-3" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
              <div className="space-y-3">
                {/* Status filter */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) => onFilterChange('status', value as EfolderStatusFilter)}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* File type filter */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">File Type</Label>
                  <Select
                    value={filters.fileType}
                    onValueChange={(value) => onFilterChange('fileType', value as EfolderFileTypeFilter)}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      {FILE_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date range filter */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Date Range</Label>
                  <Select
                    value={filters.dateRange}
                    onValueChange={(value) => onFilterChange('dateRange', value as EfolderDateRangeFilter)}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="All Time" />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_RANGE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* View mode filter */}
                <div className="space-y-1.5 pt-2 border-t">
                  <Label className="text-xs text-muted-foreground">View Mode</Label>
                  <Select
                    value={filters.groupingMode}
                    onValueChange={(value) => onFilterChange('groupingMode', value as EfolderGroupingMode)}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="View Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {VIEW_MODE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          <div className="flex flex-col">
                            <span>{option.label}</span>
                            <span className="text-[10px] text-muted-foreground">{option.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Show old versions toggle */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <Label
                    htmlFor="show-inactive-sidebar"
                    className="text-xs cursor-pointer flex items-center gap-1.5"
                  >
                    <Archive className="h-3 w-3 text-muted-foreground" />
                    Show old versions
                    {inactiveCount > 0 && !filters.showInactive && (
                      <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                        {inactiveCount}
                      </Badge>
                    )}
                  </Label>
                  <Switch
                    id="show-inactive-sidebar"
                    checked={filters.showInactive}
                    onCheckedChange={(checked) => onFilterChange('showInactive', checked)}
                  />
                </div>

                {/* Clear all button */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onResetFilters}
                    className="w-full h-7 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Clear all filters
                  </Button>
                )}

              </div>
            </PopoverContent>
          </Popover>

          {/* Sort controls */}
          <Select
            value={sort.field}
            onValueChange={(value) => onSortFieldChange(value as EfolderSortField)}
          >
            <SelectTrigger className="w-[80px] h-7 text-xs">
              <ArrowUpDown className="h-3 w-3 mr-1" />
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-xs">
                  <span className="flex items-center gap-2">
                    {option.icon}
                    {option.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort direction toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSortDirectionToggle}
                  className="h-7 w-7 p-0"
                >
                  {sort.direction === 'asc' ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{sort.direction === 'asc' ? 'Ascending' : 'Descending'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Collapse button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleCollapse}
                  className="h-7 w-7 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Collapse sidebar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Copy Mode Controls */}
        {isCopyMode ? (
          <div className="flex flex-col gap-1.5 px-2 py-1.5 border-t bg-primary/5">
            {/* Selection counts row */}
            <div className="flex items-center gap-1.5">
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {checkedAttachments.size} selected
              </Badge>
              {/* Hidden selections warning (Phase 3) - keyboard accessible */}
              {hiddenSelectionsCount > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={onClearHiddenSelections}
                        className="inline-flex items-center h-5 px-1.5 text-xs rounded-full bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                      >
                        {hiddenSelectionsCount} hidden
                        <X className="h-2.5 w-2.5 ml-1" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{hiddenSelectionsCount} selected item{hiddenSelectionsCount !== 1 ? 's' : ''} hidden by filter. Click to clear.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={onSelectAllDownloaded}
                className="h-6 px-2 text-xs"
              >
                <CheckSquare className="h-3 w-3 mr-1" />
                All
              </Button>
              <div className="flex-1" />
              {/* Download ZIP button */}
              <Button
                size="sm"
                variant="outline"
                onClick={onBulkDownload}
                disabled={checkedAttachments.size === 0 || downloading}
                className="h-6 px-2 text-xs"
              >
                {downloading ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Download className="h-3 w-3 mr-1" />
                )}
                {downloading ? 'Creating...' : 'Download'}
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={onOpenCopyModal}
                disabled={checkedAttachments.size === 0 || downloading}
                className="h-6 px-2 text-xs"
              >
                <Copy className="h-3 w-3 mr-1" />
                Copy
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onExitCopyMode}
                disabled={downloading}
                className="h-6 px-2 text-xs"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-2 py-1.5 border-t">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onEnterCopyMode}
                    disabled={downloadedCount === 0}
                    className="w-full h-7 text-xs"
                  >
                    <Copy className="h-3 w-3 mr-1.5" />
                    Select to Copy/Download
                  </Button>
                </TooltipTrigger>
                {downloadedCount === 0 && (
                  <TooltipContent>
                    <p>No downloaded documents to copy</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {/* Tree view */}
      <div className="flex-1 overflow-y-auto overscroll-contain p-2">
        <EfolderTreeView
          documents={documents}
          groupedDocuments={groupedDocuments}
          groupingMode={filters.groupingMode}
          selectedAttachmentId={selectedAttachmentId}
          onSelectAttachment={onSelectAttachment}
          onViewAttachment={onViewAttachment}
          onDownloadAttachment={onDownloadAttachment}
          isCopyMode={isCopyMode}
          checkedAttachments={checkedAttachments}
          onToggleCheck={onToggleCheck}
          onToggleGroupCheck={onToggleGroupCheck}
          onToggleFolderCheck={onToggleFolderCheck}
          getGroupSelectionState={getGroupSelectionState}
          getFolderSelectionState={getFolderSelectionState}
          getCopyableCountForGroup={getCopyableCountForGroup}
          searchQuery={filters.searchQuery}
        />
      </div>

      {/* Footer with count */}
      <div className="p-2 border-t bg-background/50">
        <div className="text-xs text-muted-foreground text-center">
          {filteredCount === totalCount ? (
            <span>{totalCount} documents</span>
          ) : (
            <span>
              Showing <span className="font-medium text-foreground">{filteredCount}</span> of {totalCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
